/**
 * Whether this browser may evict the vault, and what to say about it.
 *
 * A browser is free to throw away IndexedDB under storage pressure unless the
 * origin has been granted persistence. Asking is cheap, but asking before the
 * vault holds anything is asking for nothing; the first successful sync is the
 * moment the local copy becomes worth protecting, so that is when the request
 * is made. A refusal is not an error — it is a fact the sync state has to say
 * out loud, because a vault that can vanish is not the offline vault the
 * reader thinks they have.
 */

/** The subset of `navigator.storage` this needs; optional because Safari's worker scope lacks it. */
export interface DurableStorage {
  readonly persist?: () => Promise<boolean>;
  readonly persisted?: () => Promise<boolean>;
}

/**
 * Asks for durable storage unless it is already granted. Resolves to whether
 * the vault is protected afterwards; any browser refusal reads as `false`.
 */
export async function ensureDurableStorage(storage: DurableStorage | undefined): Promise<boolean> {
  if (!storage) return false;
  try {
    if (storage.persisted && await storage.persisted()) return true;
    if (!storage.persist) return false;
    return await storage.persist();
  } catch {
    return false;
  }
}

/** The warning the sync state carries while the vault is unprotected. */
export const EVICTION_NOTICE = "storage may be evicted";

/**
 * The connection label with the eviction warning attached when it applies.
 * `undefined` protection means the vault has not reported yet, and a warning
 * about a state nobody has checked would be noise.
 */
export function connectionLabelWithDurability(label: string, persistent: boolean | undefined): string {
  return persistent === false ? `${label} · ${EVICTION_NOTICE}` : label;
}
